import { useEffect, useState } from "react";
import { emailBuilderService } from "@/lib/api-services/email-builder-service";
import type { EmailDocument } from "@/types/email";

interface PageResult {
  page: number;
  documents: EmailDocument[];
  totalPages: number;
  error: string | null;
}

/**
 * Loads one page of the account's email documents. Responses for a page the user has already
 * moved away from are ignored, so a slow earlier request can't overwrite a newer one.
 */
export function usePagedDocuments(page: number, pageSize: number, errorMessage = "Failed to load emails") {
  const [result, setResult] = useState<PageResult | null>(null);

  useEffect(() => {
    let cancelled = false;

    emailBuilderService
      .getDocuments({ page, pageSize })
      .then((paged) => {
        if (!cancelled) setResult({ page, documents: paged.items, totalPages: paged.totalPages, error: null });
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setResult({ page, documents: [], totalPages: 1, error: err instanceof Error ? err.message : errorMessage });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, pageSize, errorMessage]);

  const current = result?.page === page ? result : null;
  return {
    documents: current?.documents ?? [],
    // Keep the previous page count while the next page loads, so pagination doesn't jump
    totalPages: result?.totalPages ?? 1,
    isLoading: current === null,
    error: current?.error ?? null,
  };
}
